import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { environment } from '../../../environments/environment';

const SCRIPT_URL = environment.squareScriptUrl;

const SquareGooglePay = forwardRef(function SquareGooglePay(
  {
    applicationId = environment.squareApplicationId,
    currentStoreId = undefined,
    orderTotal = 0,
    onToken,
    onError,
  },
  ref
) {
  const effectiveLocationId = currentStoreId;
  const googlePayRef = useRef(null);
  const paymentsRef = useRef(null);
  const paymentRequestRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const isProcessingRef = useRef(false); // Use ref to track processing state
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [initError, setInitError] = useState(null);

  // Keep callback refs up to date
  useEffect(() => {
    onTokenRef.current = onToken;
    onErrorRef.current = onError;
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Don't re-initialize if payment was already successful
    if (paymentSuccess) {
      return;
    }

    let mounted = true;

    const loadScript = async () => {
      try {
        // Load Square SDK if not already loaded
        if (!window.Square) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!mounted) return;

        // Initialize Square Payments
        const payments = window.Square.payments(
          applicationId,
          effectiveLocationId
        );
        paymentsRef.current = payments;

        // Create Payment Request
        const amountInCents = Math.round(orderTotal);
        const amountFormatted = (amountInCents / 100).toFixed(2);

        const paymentRequest = payments.paymentRequest({
          countryCode: 'US',
          currencyCode: 'USD',
          total: {
            amount: amountFormatted,
            label: 'Total',
          },
        });
        paymentRequestRef.current = paymentRequest;

        // Initialize Google Pay with Payment Request
        const googlePay = await payments
          .googlePay(paymentRequest)
          .catch((error) => {
            throw new Error(
              'Google Pay is not available. It may not be enabled for this account or may not be supported in your browser.'
            );
          });

        // Attach Google Pay button to container
        const container = document.getElementById('sq-google-pay-container');
        if (!container) {
          return;
        }

        await googlePay.attach('#sq-google-pay-container', {
          buttonColor: 'default',
          buttonType: 'long', // Options: 'short', 'long'
        });

        // Add click handler to trigger Google Pay payment sheet
        // This is required per Square documentation: you must manually call tokenize()
        const googlePayButton = document.getElementById(
          'sq-google-pay-container'
        );

        if (googlePayButton) {
          googlePayButton.onclick = async (event) => {
            event.preventDefault();

            // Check processing state from ref to avoid dependency issues
            if (isProcessingRef.current) {
              return;
            }

            // Show processing overlay at start: while the GP sheet is open it will be on top;
            // when the user finishes and the sheet closes, this overlay is visible during
            // "getting the nonce" so the user knows to wait.
            isProcessingRef.current = true;
            setIsProcessing(true);

            try {
              const tokenResult = await googlePay.tokenize();

              if (tokenResult.status === 'OK') {
                setPaymentSuccess(true); // Mark payment as successful
                // Call onToken callback - parent component will handle the token processing
                onTokenRef.current?.(tokenResult.token);
              } else {
                const errorMsg =
                  tokenResult.errors?.[0]?.message || 'Tokenization failed';
                isProcessingRef.current = false;
                setIsProcessing(false);
                onErrorRef.current?.(new Error(errorMsg));
              }
            } catch (error) {
              // Don't show processing overlay for errors - user cancelled or error occurred
              isProcessingRef.current = false;
              setIsProcessing(false);
              onErrorRef.current?.(error);
            }
          };
        }

        googlePayRef.current = googlePay;
        setReady(true);
        setInitError(null);
      } catch (err) {
        let errorMessage = 'Failed to initialize Google Pay';
        if (err.message) {
          errorMessage = err.message;
        }
        if (err.message && err.message.includes('not available')) {
          errorMessage =
            'Google Pay is not available for this location or browser';
        }
        if (err.message && err.message.includes('merchant')) {
          errorMessage = 'Google Pay is not enabled for this merchant account';
        }

        setInitError(errorMessage);
        onErrorRef.current?.(err);
      }
    };

    loadScript();

    return () => {
      mounted = false;
      // Clean up: detach button and destroy instance
      if (googlePayRef.current) {
        try {
          // Clear the container to remove the button
          const container = document.getElementById('sq-google-pay-container');
          if (container) {
            container.innerHTML = '';
            container.onclick = null;
          }

          // Only destroy if not processing a payment to avoid "already been destroyed" error
          if (!isProcessingRef.current) {
            googlePayRef.current.destroy();
          }
        } catch (e) {
          // Error handled silently
        }
      }
    };
  }, [applicationId, effectiveLocationId, orderTotal, paymentSuccess]);

  useImperativeHandle(
    ref,
    () => ({
      tokenize: async () => {

        if (!googlePayRef.current) {

          throw new Error('Google Pay not initialized');
        }

        setIsProcessing(true);

        try {
          const result = await googlePayRef.current.tokenize();
          setIsProcessing(false);

          if (result.status === 'OK') {
            onTokenRef.current?.(result.token);
            return result.token;
          }

          const message =
            (result.errors && result.errors[0] && result.errors[0].message) ||
            'Google Pay tokenization failed';
          const err = new Error(message);
          onErrorRef.current?.(err);
          throw err;
        } catch (error) {
          setIsProcessing(false);
          onErrorRef.current?.(error);
          throw error;
        }
      },
      isReady: () => ready,
    }),
    [ready]
  );

  if (!applicationId || !effectiveLocationId) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error" sx={{ fontFamily: 'Jost' }}>
          Google Pay configuration is missing. Please check your environment
          settings or provide a currentStoreId.
        </Typography>
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error" sx={{ fontFamily: 'Jost', fontSize: '14px' }}>
          {initError}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Jost',
            fontSize: '12px',
            color: '#666',
            mt: 1,
          }}
        >
          Google Pay may not be available in your region, browser, or for this
          location.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Success Message */}
      {paymentSuccess && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '30px 20px',
            gap: 2,
            backgroundColor: '#f0f9ff',
            borderRadius: '12px',
            border: '2px solid #22c55e',
          }}
        >
          <Box
            sx={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '8px',
            }}
          >
            <Typography
              sx={{
                fontSize: '32px',
                color: 'white',
              }}
            >
              ✓
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: 'Jost',
              fontSize: '18px',
              fontWeight: 600,
              color: '#16a34a',
            }}
          >
            Payment Method Authorized!
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Jost',
              fontSize: '14px',
              color: '#666',
              textAlign: 'center',
            }}
          >
            Your Google Pay payment method has been authorized. Payment will be processed when your order is confirmed.
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {!ready && !paymentSuccess && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            gap: 2,
          }}
        >
          <CircularProgress size={32} sx={{ color: environment.mainColor }} />
          <Typography
            sx={{
              fontFamily: 'Jost',
              fontSize: '14px',
              color: '#666',
            }}
          >
            Loading Google Pay...
          </Typography>
        </Box>
      )}

      {/* Google Pay button - hidden after successful payment */}
      {!paymentSuccess && (
        <div
          id="sq-google-pay-container"
          style={{
            minHeight: ready ? '60px' : '0px',
            opacity: ready ? 1 : 0,
            transition: 'opacity 0.3s ease',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
      )}

      {/* Processing Overlay */}
      {isProcessing && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#fff',
              padding: '32px',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: '300px',
            }}
          >
            <CircularProgress
              size={48}
              sx={{ color: environment.mainColor, marginBottom: '16px' }}
            />
            <Typography
              sx={{
                fontFamily: 'Jost',
                fontSize: '18px',
                fontWeight: 600,
                color: '#1A2341',
                textAlign: 'center',
                marginBottom: '8px',
              }}
            >
              Processing Google Pay Payment...
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost',
                fontSize: '14px',
                color: '#7A869A',
                textAlign: 'center',
              }}
            >
              Please wait while we securely process your payment
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default SquareGooglePay;
